import React from 'react';
import {
  BuyItNowExpressPayOptionsFieldsFragment,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
  core_apimessages_Error as Error,
} from '@reverbdotcom/commons/src/gql/graphql';
import BuyItNowApplePayWrapper from './BuyItNowApplePayWrapper';
import { ListingGooglePayButton } from './checkout/ListingGooglePayButton';
import { ExpressPayPalButtons } from './checkout/ExpressPayPalButtons';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { trackBuyNowOptionClick } from './checkout/buyNowTracking';
import { isViaPayPal } from './checkout/isViaPaypal';
import { gql } from '@reverbdotcom/commons/src/gql';

type Listing = BuyItNowExpressPayOptionsFieldsFragment;

interface Props {
  paymentMethods: CheckoutPaymentMethodType[];
  onError: (errors: Error[]) => void;
  listing: Listing;
  quantity?: number;
  protectionPlanId?: string;
  onPaymentProcessing: (checkoutId: string) => void;
}

export function BuyItNowExpressPayOptions({
  paymentMethods,
  onError,
  listing,
  quantity = null,
  protectionPlanId = null,
  onPaymentProcessing,
}: Props) {
  const primaryPayPalPaymentMethod = paymentMethods.find((paymentMethod) => isViaPayPal(paymentMethod));

  const user = useUser();

  function isAvailable(paymentMethodType: CheckoutPaymentMethodType) {
    return paymentMethods.includes(paymentMethodType);
  }

  function trackPayPalClick(paymentMethod: CheckoutPaymentMethodType) {
    trackBuyNowOptionClick({
      listingId: listing.id,
      user,
      paymentMethod,
    });
  }

  return (
    <div className="buy-it-now-express-pay-options">
      {!!primaryPayPalPaymentMethod &&
        <ExpressPayPalButtons
          currency={listing.expressPay.paypal.currency}
          checkoutBundlingId={listing.expressPay.checkoutBundlingId}
          onError={onError}
          quantity={quantity}
          amountCents={listing.pricing.buyerPrice.amountCents}
          parentComponentName="BuyItNowExpressPayOptions"
          showPaypalPayLaterButton={isAvailable(CheckoutPaymentMethodType.PAYPAL_PAY_LATER)}
          paymentMethod={primaryPayPalPaymentMethod}
          onClick={trackPayPalClick}
          protectionPlanId={protectionPlanId}
          small
        />
      }
      {isAvailable(CheckoutPaymentMethodType.ANDROID_PAY) &&
        <ListingGooglePayButton
          listing={listing}
          quantity={quantity}
          protectionPlanId={protectionPlanId}
          onPaymentProcessing={onPaymentProcessing}
          small
        />
      }
      {isAvailable(CheckoutPaymentMethodType.APPLE_PAY) &&
        <BuyItNowApplePayWrapper
          listingId={listing.id}
          listingCurrencyCode={listing.currency}
          paymentMethodsConfig={listing.adyenCheckoutPaymentMethodsConfig}
          quantity={quantity}
          protectionPlanId={protectionPlanId}
          onPaymentProcessing={onPaymentProcessing}
          small
        />
      }
    </div>
  );
}

export const BuyItNowExpressPayOptionsFragment = gql(`
  fragment BuyItNowExpressPayOptionsFields on Listing {
    _id
    id
    currency
    adyenCheckoutPaymentMethodsConfig
    expressPay {
      _id
      checkoutBundlingId
      paypal {
        currency
      }
    }
    pricing {
      buyerPrice {
        amount
        amountCents
      }
    }
    ...ListingGooglePayButtonFields
  }
`);

